import { GetColours } from "@/constants/priorityColours"
import {
  configureStore,
  createAction,
  createEntityAdapter,
  createReducer,
  createSelector,
} from "@reduxjs/toolkit"
import { Namespace, TFunction } from "i18next"
import { Brand, identity } from "ts-brand"
import {
  LearnersStudentPreferencesSubjectPreferenceSet,
  LearnersStudentPreferencesSubjectPreferenceSetWithTAMSTrait,
} from "@/types/serializers"
import { createContext } from "react"
import {
  createDispatchHook,
  createSelectorHook,
  ReactReduxContextValue,
} from "react-redux"

type PageStore = ReturnType<typeof createPageStore>
export type PageDispatch = PageStore["dispatch"]
export type PageState = ReturnType<PageStore["getState"]>
export type PageThunk<T = void> = (
  dispatch: PageDispatch,
  getState: () => PageState,
) => T
const createPageSelector = createSelector.withTypes<PageState>()
export const PageReduxContext =
  createContext<ReactReduxContextValue<any> | null>(null)
export const usePageSelector =
  createSelectorHook(PageReduxContext).withTypes<PageState>()
export const usePageDispatch =
  createDispatchHook(PageReduxContext).withTypes<PageDispatch>()

export type ShowPreferences = LearnersStudentPreferencesSubjectPreferenceSet &
  LearnersStudentPreferencesSubjectPreferenceSetWithTAMSTrait

export type SubjectId = Brand<string, "SubjectId">
export const SubjectId = ({ id }: { id: number }) =>
  identity<SubjectId>(`${id}`)
export type SubjectEntity = Omit<ShowPreferences, "preferences" | "classes">
const subjectsAdapter = createEntityAdapter<SubjectEntity, SubjectId>({
  selectId: (subject) => SubjectId({ id: subject.id }),
})

export type PreferenceId = Brand<string, "PreferenceId">
export const PreferenceId = ({ id }: { id: number }) =>
  identity<PreferenceId>(String(id))
export type PreferenceEntity = Omit<
  ShowPreferences["preferences"][number],
  "priorities" | "classDocument" | "esClassListingId"
> & {
  classId: ClassId
  subjectId: SubjectId
}
const preferencesAdapter = createEntityAdapter<PreferenceEntity, PreferenceId>({
  selectId: (preference) => PreferenceId({ id: preference.id }),
  sortComparer: (a, b) => a.rank - b.rank,
})

export type PreferencePriorityEntity =
  ShowPreferences["preferences"][number]["priorities"][number] & {
    preferenceId: PreferenceId
    classId: ClassId
    subjectId: SubjectId
  }
const preferencePriorityAdapter = createEntityAdapter<
  PreferencePriorityEntity,
  ClassPriorityId
>({
  selectId: (priority) => ClassPriorityId({ id: priority.priority }),
})

export type ClassId = Brand<string, "CustomClassId">
export const ClassId = ({
  backendId,
  teacherId,
}: {
  backendId: string
  teacherId: string | null
}) => identity<ClassId>(`${backendId}-${teacherId ?? "unknown_teacher"}`)
export type ClassEntity = Omit<
  NonNullable<ShowPreferences["preferences"][number]["classDocument"]>,
  "subjectId" | "timetablePriorities"
> & {
  subjectId: SubjectId
  educatorName: string
  uuid: string
  teacherId: string | null
}
const classesAdapter = createEntityAdapter<ClassEntity, ClassId>({
  selectId: (classEntity) =>
    ClassId({ backendId: classEntity.uuid, teacherId: classEntity.teacherId }),
})

export type ClassPriorityId = Brand<string, "PriorityId">
export const ClassPriorityId = ({ id }: { id: string }) =>
  identity<ClassPriorityId>(id)
export type ClassPriorityEntity = NonNullable<
  ShowPreferences["preferences"][number]["classDocument"]
>["timetablePriorities"][number] & {
  classId: ClassId
}
const classPrioritiesAdapter = createEntityAdapter<
  ClassPriorityEntity,
  ClassPriorityId
>({
  selectId: (priority) => ClassPriorityId({ id: priority.uuid }),
})

const subjectsLoaded = createAction<Array<ShowPreferences>>("subjectsLoaded")
export const preferenceHovered = createAction<PreferenceEntity | null>(
  "preferenceHovered",
)
export const priorityHovered = createAction<ClassPriorityEntity | null>(
  "priorityHovered",
)

const pageReducer = createReducer(
  {
    subjects: subjectsAdapter.getInitialState(),
    preferences: preferencesAdapter.getInitialState(),
    preferencePriorities: preferencePriorityAdapter.getInitialState(),
    classes: classesAdapter.getInitialState(),
    classPriorities: classPrioritiesAdapter.getInitialState(),
    calendar: {
      cycleLength: 7,
      startInstant: "2025-01-06T00:00:00Z",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    highlighting: {
      highlightedPreferenceId: null as PreferenceId | null,
      highlightedPriorityId: null as ClassPriorityId | null,
    },
  },
  (builder) => {
    builder.addCase(subjectsLoaded, (state, action) => {
      action.payload.forEach(({ preferences, ...subjectEntity }) => {
        const subjectId = SubjectId({ id: subjectEntity.id })
        subjectsAdapter.setOne(state.subjects, subjectEntity)

        preferences.forEach(
          ({
            esClassListingId,
            esTeacherId,
            priorities,
            classDocument,
            ...preference
          }) => {
            if (classDocument === null) return

            const classId = ClassId({
              backendId: esClassListingId,
              teacherId: esTeacherId,
            })
            const { timetablePriorities, ...customClass } = classDocument
            if (customClass.cycleLength > state.calendar.cycleLength) {
              state.calendar.cycleLength = customClass.cycleLength
            }

            classesAdapter.setOne(state.classes, {
              ...customClass,
              uuid: esClassListingId,
              teacherId: esTeacherId,
              subjectId,
              educatorName:
                customClass.teacherName !== null
                  ? `${customClass.teacherName} (${customClass.listingProfileName})`
                  : customClass.listingProfileName,
            })

            classPrioritiesAdapter.setMany(
              state.classPriorities,
              timetablePriorities.map((timetablePriority) => ({
                classId: classId,
                ...timetablePriority,
              })),
            )
            preferencesAdapter.setOne(state.preferences, {
              subjectId,
              classId,
              esTeacherId,
              ...preference,
            })

            preferencePriorityAdapter.setMany(
              state.preferencePriorities,
              priorities.map(({ ...preferencePriorityDetails }) => ({
                preferenceId: PreferenceId({ id: preference.id }),
                subjectId: subjectsAdapter.selectId(subjectEntity),
                classId,
                ...preferencePriorityDetails,
              })),
            )
          },
        )
      })
    })

    builder.addCase(preferenceHovered, (state, action) => {
      state.highlighting.highlightedPreferenceId = action.payload
        ? preferencesAdapter.selectId(action.payload)
        : null
    })

    builder.addCase(priorityHovered, (state, action) => {
      state.highlighting.highlightedPriorityId = action.payload
        ? classPrioritiesAdapter.selectId(action.payload)
        : null
    })
  },
)

export function createPageStore() {
  return configureStore({ reducer: pageReducer })
}

const subjectsSelectors = subjectsAdapter.getSelectors(
  (state: PageState) => state.subjects,
)
const preferencesSelectors = preferencesAdapter.getSelectors(
  (state: PageState) => state.preferences,
)
const preferencePrioritiesSelectors = preferencePriorityAdapter.getSelectors(
  (state: PageState) => state.preferencePriorities,
)
const classPrioritiesSelectors = classPrioritiesAdapter.getSelectors(
  (state: PageState) => state.classPriorities,
)
const classesSelectors = classesAdapter.getSelectors(
  (state: PageState) => state.classes,
)

export const selectSubject = (
  state: PageState,
  { subjectId }: { subjectId: SubjectId },
) => subjectsSelectors.selectById(state, subjectId)
export const selectSubjectIds = (state: PageState) =>
  subjectsSelectors.selectIds(state)

export const selectPreferenceIds = createPageSelector(
  preferencesSelectors.selectIds,
  (preferenceIds) => new Set(preferenceIds),
)

export const selectPreferencePriority = (
  state: PageState,
  { classPriority }: { classPriority: ClassPriorityEntity },
) =>
  preferencePrioritiesSelectors.selectById(
    state,
    classPrioritiesAdapter.selectId(classPriority),
  )

export const selectClassPriority = (
  state: PageState,
  { priorityId }: { priorityId: ClassPriorityId },
) => classPrioritiesSelectors.selectById(state, priorityId)

const buildCalendar = (calendarState: PageState["calendar"]) => ({
  calendarTimezoneId: calendarState.timezone,
  cycleLength: calendarState.cycleLength,
  startTemporalInstant: Temporal.Instant.from(calendarState.startInstant),
  startTemporalZonedDateTime: Temporal.Instant.from(
    calendarState.startInstant,
  ).toZonedDateTimeISO("UTC"),
})
export const selectCalendar = createPageSelector(
  [(state) => state.calendar],
  buildCalendar,
)

export const selectHighlightedPreferenceId = createPageSelector(
  [(state) => state.highlighting],
  (highlightingState) => highlightingState.highlightedPreferenceId,
)

export const selectHighlightedPriorityId = createPageSelector(
  [(state) => state.highlighting],
  (highlightingState) => highlightingState.highlightedPriorityId,
)

export const selectPreference = (
  state: PageState,
  { preferenceId }: { preferenceId: PreferenceId },
) => preferencesSelectors.selectById(state, preferenceId)

export const selectPreferenceClassPriorities = createPageSelector(
  [
    (state) => state.classPriorities,
    preferencePrioritiesSelectors.selectAll,
    (_state, { preference }: { preference: PreferenceEntity }) => preference,
  ],
  (classPrioritiesState, preferencePriorities, preference) =>
    preferencePriorities
      .filter(
        ({ preferenceId }) =>
          preferencesAdapter.selectId(preference) === preferenceId,
      )
      .map(({ priority }) =>
        classPrioritiesAdapter
          .getSelectors()
          .selectById(classPrioritiesState, ClassPriorityId({ id: priority })),
      )
      .filter(Boolean),
)

export const selectPreferenceClass = createPageSelector(
  [
    (state) => classesSelectors.selectEntities(state),
    (_state, { preference }: { preference: PreferenceEntity }) => preference,
  ],
  (customClasses, preference) => customClasses[preference.classId],
)

export const selectTimetableSession = createPageSelector(
  [
    selectClassPriority,
    (_state, { timetableSessionIndex }: { timetableSessionIndex: number }) =>
      timetableSessionIndex,
  ],
  (priority, timetableSessionIndex) => priority.sessions[timetableSessionIndex],
)

function buildEventTitle({
  priority,
  tFunction,
  preference,
  subject_code,
}: {
  tFunction: TFunction<Namespace>
  subject_code: string
  preference: string
  priority: string
}): string {
  return tFunction("timetable.events.short_title_all_subjects", {
    priority,
    preference,
    subject_code,
  })
}

export const selectGetSubjectColours = createPageSelector(
  [
    preferencesSelectors.selectAll,
    (_state: PageState, { getColours }: { getColours: GetColours }) =>
      getColours,
  ],
  (preferences, getColours) => {
    return (preferenceRank: number, subjectId: SubjectId) =>
      getColours(
        subjectId,
        preferenceRank,
        preferences.filter((preference) => preference.subjectId === subjectId)
          .length,
      )
  },
)

export const selectSubjectPreferences = createPageSelector(
  [
    preferencesSelectors.selectAll,
    (_state, { subjectId }: { subjectId: SubjectId }) => subjectId,
  ],
  (preferences, subjectId) =>
    preferences.filter((preference) => preference.subjectId === subjectId),
)

export const selectFullCalendarEvents = createPageSelector(
  [
    preferencePrioritiesSelectors.selectAll,
    (state) => state.classPriorities,
    (state) => state.classes,
    (state) => state.preferences,
    (state) => state.subjects,
    (state) => state.highlighting.highlightedPreferenceId,
    (state) => state.highlighting.highlightedPriorityId,
    (_state: PageState, { tFunction }: { tFunction: TFunction<Namespace> }) =>
      tFunction,
    selectGetSubjectColours,
  ],
  (
    preferencePriorities,
    classPrioritiesState,
    classesState,
    preferencesState,
    subjectsState,
    highlightedPreferenceId,
    highlightedPriorityId,
    tFunction,
    getSubjectColours,
  ) => {
    return preferencePriorities.flatMap((preferencePriority) => {
      const classPriority = classPrioritiesAdapter
        .getSelectors()
        .selectById(
          classPrioritiesState,
          ClassPriorityId({ id: preferencePriority.priority }),
        )

      if (!classPriority) return []

      const classEntity = classesAdapter
        .getSelectors()
        .selectById(classesState, classPriority.classId)
      const subject = subjectsAdapter
        .getSelectors()
        .selectById(subjectsState, classEntity.subjectId)
      const preference = preferencesAdapter
        .getSelectors()
        .selectById(preferencesState, preferencePriority.preferenceId)

      const colours = getSubjectColours(
        preference.rank,
        subjectsAdapter.selectId(subject),
      )
      const eventTitle = buildEventTitle({
        tFunction,
        priority: classPriority.priority.toString(),
        preference: preference.rank.toString(),
        subject_code: subject.code,
      })

      return classPriority.sessions.flatMap((session, sessionIndex) => ({
        cycleLength: classEntity.cycleLength,
        id: classPrioritiesAdapter.selectId(classPriority),
        startTime: session.startTime,
        duration: session.duration,
        day: session.day,
        eventTimezoneId: classEntity.timezoneId,
        eventTitle,
        extendedProps: {
          priorityHighlighted:
            highlightedPriorityId ===
            classPrioritiesAdapter.selectId(classPriority),
          preferenceHighlighted:
            highlightedPreferenceId === preferencesAdapter.selectId(preference),
          preferenceId: preferencesAdapter.selectId(preference),
          priorityId: classPrioritiesAdapter.selectId(classPriority),
          timetableSessionIndex: sessionIndex,
          colours,
        },
      }))
    })
  },
)

export function loadTimetables({
  subjectsPayload,
}: {
  subjectsPayload: Parameters<typeof subjectsLoaded>[0]
}): PageThunk {
  return (dispatch) => {
    dispatch(subjectsLoaded(subjectsPayload))
  }
}
